import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import InfoPopup from './InfoPopup';

const Card = styled(motion.div)`
  background-color: var(--card-background);
  background-image: linear-gradient(to bottom, rgba(92, 67, 169, 0.1), rgba(26, 13, 58, 0.1));
  border-radius: var(--border-radius);
  padding: 1rem;
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  box-shadow: var(--box-shadow);
  min-height: 100px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border-left: 4px solid ${props => getTypeColor(props.$itemType)};
  position: relative;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.4);
  }
  
  &::after {
    content: 'Click for details';
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    font-size: 0.7rem;
    color: var(--text-color);
    opacity: 0;
    background-color: rgba(0, 0, 0, 0.5);
    padding: 0.2rem 0.4rem;
    border-radius: 4px;
    transition: opacity 0.2s ease;
  }
  
  &:hover::after {
    opacity: 0.7;
  }
`;

const ItemImage = styled.div`
  width: 64px;
  height: 64px;
  background-image: ${props => `url(${props.$image})`};
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 8px;
  margin-right: 1rem;
  flex-shrink: 0;
  background-color: var(--primary-color);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at center, transparent 50%, rgba(0, 0, 0, 0.3) 100%);
    border-radius: 8px;
  }
`;

const ItemDetails = styled.div`
  flex-grow: 1;
  overflow: hidden;
`;

const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
`;

const ItemTitleArea = styled.div`
  display: flex;
  flex-direction: column;
`;

const ItemName = styled.h4`
  font-size: 1.2rem;
  margin-bottom: 0.25rem;
  color: var(--text-color);
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemMeta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const ItemType = styled.span`
  background-color: ${props => getTypeColor(props.$type)};
  color: var(--text-color);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ItemCost = styled.span`
  color: gold;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  
  &::before {
    content: '';
    display: inline-block;
    width: 10px;
    height: 10px;
    background-color: gold;
    border-radius: 50%;
  }
`;

const ItemDescription = styled.p`
  margin-top: 0.5rem;
  font-size: 0.9rem;
  color: #ccc;
  line-height: 1.4;
  display: ${props => props.$expanded ? 'block' : '-webkit-box'};
  -webkit-line-clamp: ${props => props.$expanded ? 'unset' : '2'};
  -webkit-box-orient: vertical;
  overflow: hidden;
  position: relative;
  cursor: pointer;
`;

const ExpandButton = styled.button`
  background: none;
  border: none;
  color: var(--accent-color);
  font-size: 0.8rem;
  padding: 0.25rem 0;
  margin-top: 0.25rem;
  cursor: pointer;
  text-decoration: underline;
  opacity: 0.8;
  
  &:hover {
    opacity: 1;
  }
`;

const StatsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const StatItem = styled.span`
  background-color: rgba(164, 140, 240, 0.15);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  
  &::before {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    background-color: var(--accent-color);
    border-radius: 50%;
  }
`;

// Function to determine color based on item type
function getTypeColor(type) {
  switch(type?.toLowerCase()) {
    case 'starter':
      return '#4caf50'; // Green
    case 'relic':
      return '#ff9800'; // Orange
    case 'physical':
      return '#f44336'; // Red
    case 'magical':
      return '#2196f3'; // Blue
    default:
      return 'var(--primary-color)'; // Default
  }
}

const ItemCard = ({ item, type = 'Item', animationDelay = 0 }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const itemImage = item.icon_url || 'https://via.placeholder.com/64?text=Item';
  
  const toggleExpand = (e) => {
    e.stopPropagation(); // Prevent opening popup when clicking expand
    setIsExpanded(!isExpanded);
  };
  
  const handleCardClick = () => {
    setShowPopup(true);
  };
  
  return (
    <>
      <Card
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3, delay: animationDelay }}
        $itemType={type}
        onClick={handleCardClick}
      >
        <ItemImage $image={itemImage} />
        <ItemDetails>
          <ItemHeader>
            <ItemTitleArea>
              <ItemName>{item.name}</ItemName>
            </ItemTitleArea>
          </ItemHeader>
          
          <ItemMeta>
            <ItemType $type={type}>{type}</ItemType>
            {item.total_gold_cost > 0 && <ItemCost>{item.total_gold_cost}</ItemCost>}
          </ItemMeta>
          
          {item.stats && item.stats.length > 0 && (
            <StatsList>
              {item.stats.map((stat, index) => (
                <StatItem key={index}>
                  {stat.DisplayName}: {stat.Value}
                </StatItem>
              ))}
            </StatsList>
          )}
          
          <ItemDescription $expanded={isExpanded} onClick={(e) => e.stopPropagation()}>
            {item.description}
          </ItemDescription>
          
          {item.description && item.description.length > 80 && (
            <ExpandButton onClick={toggleExpand}>
              {isExpanded ? 'Show less' : 'Show more'}
            </ExpandButton>
          )}
        </ItemDetails>
      </Card>
      
      <InfoPopup
        data={item}
        isOpen={showPopup}
        onClose={() => setShowPopup(false)}
        type="item"
      />
    </>
  );
};

export default ItemCard; 